import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import TindakanComponent from './TindakanComponent'
import { SipCheck, SipInput } from '../form/SipControls'

function DataMonitoring({datamon, handleView, onMonitoringDataChange, tanggalMonitoring}) {
  const { currentUser } = useAuth()
  const [monitoringData, setMonitoringData] = useState([])

  // Effect untuk mengirim data ke parent component ketika monitoringData berubah
  useEffect(() => {
    if (onMonitoringDataChange) {
      onMonitoringDataChange(monitoringData)
    }
  }, [monitoringData, onMonitoringDataChange])

  // Fungsi untuk menangani perubahan checkbox (mutual exclusive)
  const handleCheckboxChange = (itemId, kondisi, isChecked) => {
    setMonitoringData(prevData => {
      // Hapus semua data untuk item yang sama (baik kondisi baik maupun rusak)
      const filteredData = prevData.filter(data => data.item_id !== itemId)
      
      let updatedData = filteredData;
      
      // Jika checkbox dicentang, tambahkan data baru
      if (isChecked) {
        const newData = {
          tgl: tanggalMonitoring, // tanggal monitoring dari props
          item_id: itemId,
          kondisi: kondisi, // 'baik' atau 'rusak'
          keterangan: kondisi === 'rusak' ? '' : null, // inisialisasi keterangan kosong untuk rusak
          pic: currentUser?.name,
          timestamp: new Date().toISOString()
        }
        updatedData = [...filteredData, newData]
      }

      return updatedData
    })
  }

  // Fungsi untuk menangani perubahan keterangan
  const handleKeteranganChange = (itemId, keterangan) => {
    setMonitoringData(prevData => {
      return prevData.map(data => {
        if (data.item_id === itemId) {
          return { ...data, keterangan }
        }
        return data
      })
    })
  }

  // Fungsi untuk mendapatkan keterangan item
  const getKeterangan = (itemId) => {
    const item = monitoringData.find(data => data.item_id === itemId)
    return item ? item.keterangan || '' : ''
  }

  // Fungsi untuk mengecek apakah checkbox sudah dicentang
  const isChecked = (itemId, kondisi) => {
    return monitoringData.some(data => 
      data.item_id === itemId && data.kondisi === kondisi
    )
  }

  // Fungsi untuk mengecek apakah item sudah memiliki kondisi yang dipilih
  const hasCondition = (itemId) => {
    return monitoringData.some(data => data.item_id === itemId)
  }

  // Fungsi untuk mendapatkan kondisi yang dipilih untuk item tertentu
  const getSelectedCondition = (itemId) => {
    const item = monitoringData.find(data => data.item_id === itemId)
    return item ? item.kondisi : null
  }

  return (
    <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Kode Inventaris
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Deskripsi
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Baik
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Rusak
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Keterangan
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {datamon.map((item, index) => (
                    <tr key={`monitoring-item-${item.id || item.kode || index}`} className="hover:bg-gray-50 transition-colors duration-200 animate__animated animate__fadeIn" style={{animationDelay: `${index * 0.1}s`}}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900">{item.kode || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{item.kategori || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-medium">{item.desc || 'Tidak ada deskripsi'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center">
                          <SipCheck
                            inputId={`baik-${item.id}`}
                            checked={isChecked(item.id, 'baik')}
                            onChange={(e) => handleCheckboxChange(item.id, 'baik', e.target.checked)}
                            disabled={hasCondition(item.id) && !isChecked(item.id, 'baik')}
                          />
                          <label 
                            htmlFor={`baik-${item.id}`} 
                            className={`ml-2 text-sm font-medium cursor-pointer transition-all duration-200 ${
                              isChecked(item.id, 'baik') 
                                ? 'text-success' 
                                : hasCondition(item.id) 
                                  ? 'text-gray-400' 
                                  : 'text-success'
                            }`}
                          >
                            Baik
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center">
                          <SipCheck
                            inputId={`rusak-${item.id}`}
                            checked={isChecked(item.id, 'rusak')}
                            onChange={(e) => handleCheckboxChange(item.id, 'rusak', e.target.checked)}
                            disabled={hasCondition(item.id) && !isChecked(item.id, 'rusak')}
                          />
                          <label 
                            htmlFor={`rusak-${item.id}`} 
                            className={`ml-2 text-sm font-medium cursor-pointer transition-all duration-200 ${
                              isChecked(item.id, 'rusak') 
                                ? 'text-danger' 
                                : hasCondition(item.id) 
                                  ? 'text-gray-400' 
                                  : 'text-danger'
                            }`}
                          >
                            Rusak
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {isChecked(item.id, 'rusak') ? (
                          <SipInput
                            value={getKeterangan(item.id)}
                            onChange={(e) => handleKeteranganChange(item.id, e.target.value)}
                            placeholder="Masukkan keterangan..."
                          />
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-info/10 text-info">
                            {item.keterangan || item.total || ((item.kondisi_baik || item.baik || 0) + (item.kondisi_rusak || item.rusak || 0))}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {isChecked(item.id, 'rusak') && (
                            <TindakanComponent id={item.id} handleView={handleView} tgl={tanggalMonitoring}/>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
  )
}

export default DataMonitoring